const STYLE_ID = "tk-disclaimer-styles";

const DEFAULT_TEXT =
  "This 3D rendering is intended for visualization purposes only. " +
  "The details of this 3D rendering – including, without limitation, the dimensions and the " +
  "placement, size, and proportionality of certain components in the image shown – may differ " +
  "from the actual product. Certain components and features may also be omitted from the 3D " +
  "rendering even if they are selected during configuration.";

function ensureDisclaimerStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.tk-disclaimer {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 15;
  max-width: min(420px, calc(100% - 20px));
  pointer-events: auto;
  font: 400 11px/1.4 system-ui, sans-serif;
  color: #fff;
}
.tk-disclaimer__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font: inherit;
  cursor: pointer;
  transition: background 0.15s;
}
.tk-disclaimer__tab:hover {
  background: rgba(0, 0, 0, 0.7);
}
.tk-disclaimer__tab:focus-visible {
  outline: 2px solid #1a56db;
  outline-offset: 2px;
}
.tk-disclaimer__panel {
  display: none;
  position: relative;
  padding: 10px 30px 10px 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
}
.tk-disclaimer__panel p {
  color: #fff !important;
}
.tk-disclaimer--open .tk-disclaimer__tab {
  display: none;
}
.tk-disclaimer--open .tk-disclaimer__panel {
  display: block;
}
.tk-disclaimer__close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.tk-disclaimer__close:hover {
  background: rgba(255, 255, 255, 0.15);
}
`;
  document.head.appendChild(style);
}

export interface DisclaimerOverlayOptions {
  /** Overrides the default legal disclaimer text. */
  text?: string;
  /** Start expanded instead of collapsed to the small tab (default `true`). */
  defaultOpen?: boolean;
}

export interface DisclaimerOverlayHandle {
  detach(): void;
}

/**
 * Overlays a collapsible legal disclaimer in the bottom-left of the player shell, stating that
 * the 3D rendering is for visualization only and may differ from the actual product. Starts
 * expanded (pass `defaultOpen: false` to start collapsed to a small "Disclaimer" tab instead).
 * Collapsed or expanded, it's confined to the bottom-left corner so it never covers the player's
 * own bottom-right controls (help / fullscreen) — those are drawn into the WebGL canvas itself,
 * so they can't be repositioned via CSS. Click the tab to open it; click the close button to
 * collapse it back.
 */
export function attachDisclaimerOverlay(
  shell: HTMLElement,
  options: DisclaimerOverlayOptions = {}
): DisclaimerOverlayHandle {
  ensureDisclaimerStyles();

  if (getComputedStyle(shell).position === "static") {
    shell.style.position = "relative";
  }

  const text = options.text ?? DEFAULT_TEXT;

  const wrap = document.createElement("div");
  wrap.className = "tk-disclaimer";

  const tab = document.createElement("button");
  tab.type = "button";
  tab.className = "tk-disclaimer__tab";
  tab.setAttribute("aria-expanded", "false");
  tab.textContent = "Disclaimer";

  const panel = document.createElement("div");
  panel.className = "tk-disclaimer__panel";
  panel.setAttribute("role", "note");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "tk-disclaimer__close";
  closeBtn.setAttribute("aria-label", "Collapse disclaimer");
  closeBtn.innerHTML = "&times;";

  const body = document.createElement("p");
  body.style.margin = "0";
  body.textContent = text;

  panel.appendChild(closeBtn);
  panel.appendChild(body);
  wrap.appendChild(tab);
  wrap.appendChild(panel);

  function setOpen(open: boolean): void {
    wrap.classList.toggle("tk-disclaimer--open", open);
    tab.setAttribute("aria-expanded", open ? "true" : "false");
  }

  tab.addEventListener("click", () => setOpen(true));
  closeBtn.addEventListener("click", () => setOpen(false));

  setOpen(options.defaultOpen !== false);

  shell.appendChild(wrap);

  return {
    detach() {
      wrap.remove();
    },
  };
}
